import helpIcon from '@disy/cadenza-icons/help.svg';
import h from 'hyperscript';

import { createButton } from 'ui/button/button';

import { logger } from 'cadenza/utils/logging';
import type { HelpTopicLinkCallback } from 'cadenza/api-client/help-topic-link-api';
import { getHelpTopicUrlForId } from 'cadenza/api-client/help-topic-link-api';
import { icon } from 'cadenza/utils/icon/icon';
import { setupShadowOnStickyElement } from 'cadenza/utils/scroll-shadow/scroll-shadow';
import { openUrl } from 'cadenza/utils/open-url';
import { assertType } from 'cadenza/utils/custom-error';

import i18n from './help-topic-link.properties';
import './help-topic-link.css';

// Note: The documentation snippets above each ID are included in web-theming.adoc
/* eslint-disable @typescript-eslint/indent */
const HELP_TOPIC_IDS = [
/* tag::help-topics[]
| cch-import-wizard--attributes.html | On the `More Attributes` page of the `Import Data` wizard for importing files to Cadenza Workbooks.
end::help-topics[] */
  'import-wizard--attributes',
/* tag::help-topics[]
| cch-import-wizard--file.html | On the `File` page of the `Import Data` wizard for importing files to Cadenza Workbooks.
end::help-topics[] */
  'import-wizard--file',
/* tag::help-topics[]
| cch-import-wizard--contents.html | On the `Contents` page of the `Import Data` wizard for importing files to Cadenza Workbooks.
end::help-topics[] */
  'import-wizard--contents',
/* tag::help-topics[]
| cch-import-wizard--file-interpretation.html | On the `Interpretation` page of the `Import Data` wizard for importing files to Cadenza Workbooks.
end::help-topics[] */
  'import-wizard--file-interpretation',
/* tag::help-topics[]
| cch-import-wizard--objecttype.html | On the `Object type` page of the `Import Data` wizard for importing files to Cadenza Workbooks.
end::help-topics[] */
  'import-wizard--objecttype',
/* tag::help-topics[]
| cch-import-wizard--spatial-reference.html | On the `Spatial` page of the `Import Data` wizard for importing files to Cadenza Workbooks.
end::help-topics[] */
  'import-wizard--spatial-reference',
/* tag::help-topics[]
| cch-import-wizard--temporal-reference.html | On the `Temporal` page of the `Import Data` wizard for importing files to Cadenza Workbooks.
end::help-topics[] */
  'import-wizard--temporal-reference',
/* tag::help-topics[]
| cch-import-wizard--validation.html | On the `Validation` page of the `Import Data` wizard for importing files to Cadenza Workbooks.
end::help-topics[] */
  'import-wizard--validation',
/* tag::help-topics[]
| cch-import-wizard--data-protection.html | On the `Data protection` page of the `Import Data` wizard for importing files to Cadenza Workbooks.
end::help-topics[] */
  'import-wizard--data-protection',
/* tag::help-topics[]
| cch-query-editor--cql-syntax.html | On the dialog for creating or editing a query condition in Cadenza Workbooks.
end::help-topics[] */
  'query-editor--cql-syntax',
/* tag::help-topics[]
| cch-filters--ela-like-query-syntax.html | Next to the `Pattern` option of a text pattern condition created for an Elasticsearch attribute in Cadenza Workbooks.
end::help-topics[] */
  'filters--ela-like-query-syntax',
/* tag::help-topics[]
| cch-animation-settings.html | In the preview of the date filter map animation.
end::help-topics[] */
  'animation-settings',
/* tag::help-topics[]
| cch-enrichment--measures-from-spatial-overlay.html | In the "Data enrichment by spatial overlay" dialog.
end::help-topics[] */
  'enrichment--measures-from-spatial-overlay',
/* tag::help-topics[]
| cch-workbook-dialog--simplified-operation.html | On the "Simplified operation" page of the workbook management dialog.
end::help-topics[] */
  'workbook-dialog--simplified-operation',
/* tag::help-topics[]
| cch-mc-ot--attribute-relations.html | In the dialog for managing attribute relations.
end::help-topics[] */
  'mc-ot--attribute-relations',
/* tag::help-topics[]
| cch-mc-ot--function-formula.html | In the dialog for editing the formula of a function attribute.
end::help-topics[] */
  'mc-ot--function-formula'
] as const;
/* eslint-enable @typescript-eslint/indent */

type HelpTopicId = typeof HELP_TOPIC_IDS[number];

let customHelpTopicLinkCallback: HelpTopicLinkCallback;

/* For testing and catalog */
export function __setHelpTopicLinkCallback__ (callbackFunction: HelpTopicLinkCallback) {
  customHelpTopicLinkCallback = callbackFunction;
}

function getHelpTopicUrl (topicId: string): Promise<string> {
  return (customHelpTopicLinkCallback ?? getHelpTopicUrlForId)(topicId);
}

const COMPONENT_NAME = 'd-help-topic-link';

interface HelpTopicLinkOptions {
  topicId: HelpTopicId;
  linkText?: string;
  variant?: HelpTopicLinkVariant;
  title?: string;
}

type HelpTopicLinkVariant = 'textlink';

export class HelpTopicLink extends HTMLElement {

  readonly #options?: HelpTopicLinkOptions;

  /**
   * Creates a new instance.
   *
   * @param options - Optional options object
   * @param options.topicId - A mandatory topicId for identifying the helpPage linked with this helpTopicLink
   * @param options.linkText = i18n('defaultLinkText')] - An optional text used for the link button - If unset, the defaultLinkText is used, currently 'Hilfe'/'Help'
   * @param options.variant - An optional variant, currently only 'textlink' is supported
   * @param options.title - An optional title, used for the variant 'textlink' only atm.
   */
  constructor (options?: HelpTopicLinkOptions) {
    super();
    this.#options = options;
  }

  connectedCallback () {
    this.classList.add(COMPONENT_NAME);
    if (!this.#topicId) {
      logger.warn('This help topic link has no topicId ("data-topic-id" attribute).');
      return;
    }

    if (!this.#initialized) {
      getHelpTopicUrl(this.#topicId)
        .then((topicUrl: string) => {
          const linkInnerContent = this.#linkInnerContent;
          const helpTopicLinkElement = this.#variant === 'textlink'
            ? this.#createHelpTopicTextLinkElement(topicUrl, linkInnerContent)
            : this.#createHelpTopicLinkElement(topicUrl, linkInnerContent);
          this.innerHTML = '';
          this.append(helpTopicLinkElement);
          this.#setTitleIfConfigured();
          this.#setInitialized();
          setupShadowOnStickyElement(this, getScrollParent(this));
        })
        .catch(error => {
          logger.debug(`An error happened while getting the url to the help topic with id ['${this.#topicId}'].`, error);
        });
    }
  }

  #createHelpTopicTextLinkElement (topicUrl: string, linkInnerContent: NodeListOf<ChildNode> | string[]): HTMLElement {
    return h('a', { href: topicUrl, target: '_blank' }, ...linkInnerContent);
  }

  #createHelpTopicLinkElement (topicUrl: string, linkInnerContent: NodeListOf<ChildNode> | string[]): HTMLElement {
    return createButton([ icon(helpIcon), ...linkInnerContent ], {
      variant: 'borderless',
      onclick: () => openUrl(topicUrl),
      styleClass: 'help-button',
      size: 's'
    });
  }

  #setTitleIfConfigured () {
    if (this.#options?.title) {
      this.setAttribute('title', this.#options.title);
    }
  }

  get #topicId () {
    const topicId = this.#options?.topicId ?? this.dataset.topicId;
    if (topicId) {
      assertType<HelpTopicId>(topicId, HELP_TOPIC_IDS.includes(topicId as HelpTopicId));
      return topicId;
    }
  }

  get #linkInnerContent () {
    if (this.#options) {
      return [ this.#options.linkText ?? i18n('defaultLinkText') ];
    }
    return this.childNodes;
  }

  get #variant (): HelpTopicLinkVariant | undefined {
    if (this.#options) {
      return this.#options.variant;
    }
    return this.getAttribute('variant') as HelpTopicLinkVariant;
  }

  get #initialized () {
    return this.classList.contains('is-initialized');
  }

  #setInitialized () {
    this.classList.add('is-initialized');
  }

}

customElements.define(COMPONENT_NAME, HelpTopicLink);

function getScrollParent (node: ParentNode | null): HTMLElement | undefined {
  const isElement = node instanceof HTMLElement;
  const overflowY = isElement && window.getComputedStyle(node).overflowY;
  const isScrollable = overflowY !== 'visible' && overflowY !== 'hidden';

  if (!node) {
    return undefined;
  } else if (node instanceof HTMLElement && isScrollable && node.scrollHeight >= node.clientHeight) {
    return node;
  }

  return getScrollParent(node.parentNode);
}
